/**
 * Debridement — pure rules.
 *
 * No React, no Redux, no DOM, no network. Every export is a pure function of
 * (config, state). Kept separate from useDebridement.js so the rules are
 * directly unit-testable — the current 644-line hook is untestable precisely
 * because its logic is tangled with its state.
 */

import { PREREQUISITES, DEBRIDEMENT_TYPES, NOT_PERFORMED_REASONS } from "./debridementConfig";

/* ── policy ──────────────────────────────────────────────────────────── */

export const POLICY_LABEL = { R: "R", Rx: "R*", O: "O" };

export const POLICY_WIRE = {
  R: "Required",
  Rx: "RequiredWithOverride",
  O: "Optional",
};

/** Policy for one prerequisite in one care setting. null === hidden. */
export const policyFor = (prereq, careSetting) => prereq?.policy?.[careSetting] ?? null;

/**
 * Prerequisites this care setting renders.
 *
 * A hidden prerequisite is NOT an unmet one. Podiatry hides "provider order"
 * because the procedure is provider-performed by definition — that absence
 * must never surface as a compliance gap.
 */
export const visiblePrerequisites = (careSetting) =>
  PREREQUISITES.filter((p) => policyFor(p, careSetting) !== null);

/* ── compliance ──────────────────────────────────────────────────────── */

/**
 * Prerequisite gaps become FLAGS. They never block.
 *
 * This is the whole reason the prerequisite block exists separately. V1 ended
 * the workflow on `consent → No` and told the user to "obtain provider order
 * before proceeding" — but Block 0 already established the debridement was
 * performed. Refusing to record it produces an undocumented invasive
 * procedure, which is worse than the gap being guarded against.
 *
 * The codebase already accepts this principle: SignAndLock honours the
 * per-facility `isSignWithOutMinimumDataset` flag.
 *
 * Nothing here may ever be wired to a disabled Save button.
 */
export const complianceFlags = (careSetting, answers) => {
  const flags = [];
  visiblePrerequisites(careSetting).forEach((p) => {
    const policy = policyFor(p, careSetting);
    const answer = answers?.[p.key];

    if (answer?.value === "no") {
      flags.push({
        prerequisiteKey: p.key,
        label: p.label,
        reasonCode: answer.reasonCode || null,
        reasonText: answer.reasonText || null,
        severity: policy === "R" ? "High" : "Medium",
      });
    } else if (!answer?.value && policy === "R") {
      flags.push({
        prerequisiteKey: p.key,
        label: p.label,
        reasonCode: null,
        reasonText: "Not answered",
        severity: "Medium",
      });
    }
  });
  return flags;
};

/** Progress only. Must never gate the save. */
export const readinessCount = (careSetting, answers) => {
  const visible = visiblePrerequisites(careSetting);
  const answered = visible.filter((p) => {
    const a = answers?.[p.key];
    return a?.value === "yes" || a?.value === "na";
  }).length;
  return { answered, total: visible.length };
};

/** A "no" is only complete once a reason is attached. */
export const missingReasons = (careSetting, answers) =>
  visiblePrerequisites(careSetting)
    .filter((p) => answers?.[p.key]?.value === "no" && !answers[p.key]?.reasonCode)
    .map((p) => p.key);

/* ── procedure ───────────────────────────────────────────────────────── */

export const typeByKey = (key) => DEBRIDEMENT_TYPES.find((t) => t.key === key) || null;

/** Approach sub-list is only shown for types that define one. */
export const approachesFor = (typeKey) => typeByKey(typeKey)?.approaches ?? [];

export const requiresApproach = (typeKey) => approachesFor(typeKey).length > 0;

/** V1 nests the necrotic-subcutaneous list under exactly one depth value. */
export const showsSubcutaneousSubList = (depthKey) => depthKey === "subcutaneoustissue";

/* ── initial state ───────────────────────────────────────────────────── */

/** Hydrate editable state from the API response. Shapes the server's flat
 *  record back into the block model without losing unknown keys. */
export const stateFromResponse = (resp) => ({
  isDebridementPerformed: resp?.isDebridementPerformed ?? "yes",
  notPerformedReason: resp?.notPerformedReason ?? "",
  prerequisiteAnswers: hydratePrerequisites(resp?.prerequisites),
  surgicalDebridementProcedure: resp?.surgicalDebridementProcedure ?? "",
  nonExcisionalApproach: resp?.nonExcisionalApproach ?? "",
  depthOfDebridement: resp?.depthOfDebridement ?? "",
  necroticSubcutaneousTissue: resp?.necroticSubcutaneousTissue ?? [],
  necroticTissue: resp?.necroticTissue ?? [],
  instrumentsUsed: resp?.instrumentsUsed ?? [],
  surfaceAreaOfNecroticSkinTissue: resp?.surfaceAreaOfNecroticSkinTissue ?? "",
  surfaceAreaOfNecroticSubqTissue: resp?.surfaceAreaOfNecroticSubqTissue ?? "",
  techniqueUsed: resp?.techniqueUsed ?? "",
  painAfter: resp?.painAfter ?? "",
  postDebridementBleedingAmount: resp?.postDebridementBleedingAmount ?? "",
  stoppedBleedingContactPressure: resp?.stoppedBleedingContactPressure ?? "",
});

const hydratePrerequisites = (list) => {
  const out = {};
  (Array.isArray(list) ? list : []).forEach((p) => {
    out[p.key] = {
      value: p.answer ?? null,
      selection: p.value ?? null,
      reasonCode: p.reasonCode ?? null,
      reasonText: p.reasonText ?? null,
    };
  });
  return out;
};